import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';

interface ObraRelacionada {
  id: number;
  papel: string;
  observacao: string | null;
  proxima_acao: string | null;
  proxima_acao_em: string | null;
  obra_id: number;
  created_at: string;
  obra: {
    id: number;
    rrt_numero: string | null;
    contratante: string | null;
    cidade: string | null;
    uf: string | null;
    segmento: string | null;
    natureza_tecnica: string | null;
    em_andamento: boolean | null;
    situacao: string | null;
    valor_contrato_max: number | null;
    endereco: string | null;
  } | null;
}

interface ObraOption {
  id: number;
  rrt_numero: string | null;
  contratante: string | null;
  cidade: string | null;
  uf: string | null;
  segmento: string | null;
  natureza_tecnica: string | null;
  endereco: string | null;
}

const PAPEL_OBRA_LABELS: Record<string, string> = {
  contratante: 'Contratante',
  cliente_final: 'Cliente final',
  construtora: 'Construtora',
  incorporadora: 'Incorporadora',
  arquitetura: 'Arquitetura',
  projetista: 'Projetista',
  fornecedor: 'Fornecedor',
  parceiro: 'Parceiro',
  influenciador: 'Influenciador',
  outro: 'Outro',
};

function formatBRL(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatData(value: string | null | undefined): string {
  if (!value) return '';
  const [y, m, d] = value.slice(0, 10).split('-');
  if (!y || !m || !d) return value;
  return `${d}/${m}/${y}`;
}

function obraTitulo(o: ObraOption | ObraRelacionada['obra']): string {
  if (!o) return 'Obra';
  const partes: string[] = [];
  if (o.rrt_numero) partes.push(`RRT ${o.rrt_numero}`);
  if (o.contratante) partes.push(o.contratante);
  if (o.endereco) partes.push(o.endereco);
  return partes.join(' — ') || 'Obra';
}

export default function ClienteObrasSection({ empresaId, password }: { empresaId: number; password: string }) {
  const [relacionadas, setRelacionadas] = useState<ObraRelacionada[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [obras, setObras] = useState<ObraOption[]>([]);
  const [obrasLoading, setObrasLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedObra, setSelectedObra] = useState<number | ''>('');
  const [papel, setPapel] = useState('contratante');
  const [observacao, setObservacao] = useState('');
  const [proximaAcao, setProximaAcao] = useState('');
  const [proximaAcaoEm, setProximaAcaoEm] = useState('');
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editAcao, setEditAcao] = useState('');
  const [editAcaoEm, setEditAcaoEm] = useState('');
  const [savingAcaoId, setSavingAcaoId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-relacionamentos', {
        body: { action: 'listar_relacionados', password, empresa_id: empresaId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { obras?: ObraRelacionada[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setRelacionadas(parsed?.obras ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar obras relacionadas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, password]);

  const openModal = async () => {
    setModalOpen(true);
    setSelectedObra('');
    setPapel('contratante');
    setObservacao('');
    setProximaAcao('');
    setProximaAcaoEm('');
    setSearch('');
    setObrasLoading(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-relacionamentos', {
        body: { action: 'listar_obras', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { obras?: ObraOption[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setObras(parsed?.obras ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao listar obras.');
    } finally {
      setObrasLoading(false);
    }
  };

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return obras;
    return obras.filter(
      (o) =>
        obraTitulo(o).toLowerCase().includes(q) ||
        (o.cidade ?? '').toLowerCase().includes(q) ||
        (o.segmento ?? '').toLowerCase().includes(q),
    );
  }, [obras, search]);

  const confirmar = async () => {
    if (!selectedObra) return;
    setSaving(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-relacionamentos', {
        body: {
          action: 'relacionar_obra',
          password,
          empresa_id: empresaId,
          obra_id: Number(selectedObra),
          papel,
          observacao,
          proxima_acao: proximaAcao,
          proxima_acao_em: proximaAcaoEm,
        },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setModalOpen(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao relacionar obra.');
    } finally {
      setSaving(false);
    }
  };

  const remover = async (vinculoId: number) => {
    setRemovingId(vinculoId);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-relacionamentos', {
        body: { action: 'desrelacionar_obra', password, vinculo_id: vinculoId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao desrelacionar obra.');
    } finally {
      setRemovingId(null);
    }
  };

  const iniciarEdicao = (r: ObraRelacionada) => {
    setEditingId(r.id);
    setEditAcao(r.proxima_acao ?? '');
    setEditAcaoEm(r.proxima_acao_em ? r.proxima_acao_em.slice(0, 10) : '');
  };

  const salvarAcao = async (vinculoId: number) => {
    setSavingAcaoId(vinculoId);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-relacionamentos', {
        body: {
          action: 'atualizar_acao_obra',
          password,
          vinculo_id: vinculoId,
          proxima_acao: editAcao,
          proxima_acao_em: editAcaoEm,
        },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar próxima ação.');
    } finally {
      setSavingAcaoId(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <i className="ri-road-map-line text-[#E8610A]"></i>
          <h3 className="text-sm font-bold text-gray-900">Obras / Projetos relacionados</h3>
        </div>
        <button
          onClick={openModal}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E8610A] hover:text-[#C4500A] px-2 py-1 rounded-lg hover:bg-[#E8610A]/5 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Relacionar empresa a esta obra
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</div>
      )}

      {loading && relacionadas.length === 0 ? (
        <div className="flex justify-center py-10">
          <div className="w-7 h-7 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : relacionadas.length === 0 ? (
        <p className="text-sm text-gray-400">Nenhuma obra relacionada ainda.</p>
      ) : (
        <div className="space-y-2">
          {relacionadas.map((r) => {
            const o = r.obra;
            const isEditing = editingId === r.id;
            return (
              <div key={r.id} className="border border-gray-100 rounded-xl p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900">{obraTitulo(o)}</p>
                      <span className="text-[11px] font-semibold text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2 py-0.5 whitespace-nowrap">
                        {PAPEL_OBRA_LABELS[r.papel] ?? r.papel}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {[o?.cidade, o?.uf].filter(Boolean).join(' - ') || '—'}
                      {o?.segmento ? ` · ${o.segmento}` : ''}
                      {o?.natureza_tecnica ? ` · ${o.natureza_tecnica}` : ''}
                      {o?.em_andamento ? ' · Em andamento' : ''}
                    </p>
                    <div className="flex items-center gap-4 mt-1.5 text-xs flex-wrap">
                      <span className="text-gray-500">
                        <span className="text-gray-400">Potencial:</span>{' '}
                        <strong className="text-gray-700">{formatBRL(o?.valor_contrato_max)}</strong>
                      </span>
                    </div>

                    {isEditing ? (
                      <div className="mt-2.5 p-2.5 bg-[#E8610A]/5 border border-[#E8610A]/20 rounded-lg space-y-2">
                        <div>
                          <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">Próxima ação</label>
                          <input
                            type="text"
                            value={editAcao}
                            onChange={(e) => setEditAcao(e.target.value)}
                            placeholder="Ex: falar com diretor administrativo"
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">Data</label>
                          <input
                            type="date"
                            value={editAcaoEm}
                            onChange={(e) => setEditAcaoEm(e.target.value)}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setEditingId(null)}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer whitespace-nowrap"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={() => salvarAcao(r.id)}
                            disabled={savingAcaoId === r.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-60"
                          >
                            {savingAcaoId === r.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                            Salvar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-1.5 text-xs flex-wrap">
                        <span className="text-gray-500">
                          <span className="text-gray-400">Próxima ação:</span>{' '}
                          {r.proxima_acao ? <strong className="text-gray-700">{r.proxima_acao}</strong> : '—'}
                          {r.proxima_acao_em ? <span className="text-gray-500"> · {formatData(r.proxima_acao_em)}</span> : ''}
                        </span>
                        <button
                          onClick={() => iniciarEdicao(r)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap"
                          title="Editar próxima ação"
                        >
                          <i className="ri-edit-line"></i>
                          {r.proxima_acao ? 'Editar' : 'Definir'}
                        </button>
                      </div>
                    )}
                    {r.observacao && <p className="text-xs text-gray-500 mt-1.5 italic">{r.observacao}</p>}
                  </div>
                  <button
                    onClick={() => remover(r.id)}
                    disabled={removingId === r.id}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer disabled:opacity-50 shrink-0"
                    title="Remover relação"
                  >
                    <i className={removingId === r.id ? 'ri-loader-4-line animate-spin' : 'ri-link-unlink'}></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 text-sm">Relacionar empresa a uma obra</h3>
              <button
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Buscar obra</label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Contratante, endereço, RRT ou segmento..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Obra</label>
                {obrasLoading ? (
                  <div className="flex justify-center py-6">
                    <div className="w-6 h-6 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <select
                    value={selectedObra}
                    onChange={(e) => setSelectedObra(e.target.value ? Number(e.target.value) : '')}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]"
                  >
                    <option value="">Selecione uma obra...</option>
                    {filtradas.map((o) => (
                      <option key={o.id} value={o.id}>
                        {obraTitulo(o)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Papel da empresa</label>
                <select
                  value={papel}
                  onChange={(e) => setPapel(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]"
                >
                  {Object.entries(PAPEL_OBRA_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Próxima ação (opcional)</label>
                <input
                  type="text"
                  value={proximaAcao}
                  onChange={(e) => setProximaAcao(e.target.value)}
                  placeholder="Ex: falar com diretor administrativo"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Data da próxima ação</label>
                <input
                  type="date"
                  value={proximaAcaoEm}
                  onChange={(e) => setProximaAcaoEm(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Observação (opcional) {papel === 'outro' ? <span className="text-[#E8610A] font-normal normal-case">· recomendada para descrever o papel</span> : ''}
                </label>
                <input
                  type="text"
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  placeholder={papel === 'outro' ? 'Descreva o papel desta empresa nesta obra' : 'Ex: indicou a construtora X para este projeto'}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => setModalOpen(false)}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmar}
                  disabled={saving || !selectedObra}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-link"></i>}
                  Relacionar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}